import React, { useContext, useEffect, useState } from "react";
import { NoteAuthor, NoteSubject, NoteType } from "../../models";
import { useInfiniteQuery } from "react-query";
import NotesService from "../../service/NotesService";
import DateRangePicker from "shared/components/dateRangePicker/DateRangePicker";
import { GetNotesParameters } from "../../service/contracts";
import { ResizerContext } from "shared/stores/resizeStore/ResizerContext";
import { constants } from "shared/constants";
import { NoteListFilter } from "../../service/contracts/NoteListFilter";
import FilterNotesPanel from "./filter-notes-panel/FilterNotesPanel";
import NotesList from "./notes-list/NotesList";
import NotesListPanel from "./notes-list-panel/NotesListPanel";
import MultipleChoiceNoteFilter from "./multiple-choice-note-filter/MultipleChoiceNoteFilter";
import { EntityNoteHeader_PublicEntityType } from "shared/service/api/generated";
import { Dropdown, Flex, Input, Text } from "@fluentui/react-northstar";
import pl from "shared/messages/pl";
import ClearInputIcon from "shared/components/clearInputIcon/ClearInputIcon";
import styles from "./notesListWrapper.module.scss";
import PhraseSearchInput from "./search-notes-input/PhraseSearchInput";

export interface INotesListWrapperProps {
    salesPersonId?: number;
    intermediaryId?: number;
    selectedNoteId?: number;
    onAddNoteButtonClicked: () => void;
    onNoteSelected: (
        noteId: number | undefined,
        enitytyType: EntityNoteHeader_PublicEntityType | undefined,
        entityId: number | undefined) => void;
    availableNoteTypes: NoteType[] | undefined;
    availableNoteRetailSubjects: NoteSubject[] | undefined;
    availableNoteAuthors?: NoteAuthor[];
    isV360View: boolean;
}

export interface IAuthor {
    key: string,
    header: string,
    description: string
}

const NotesListWrapper: React.FC<INotesListWrapperProps> = (props) => {
    const resizeContext = useContext(ResizerContext);
    const isMobile = resizeContext.windowWidth < constants.view360MobileDeviceWidthPx;
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [isFirstFetch, setIsFirstFetch] = useState(true);
    const [authorQuery, setAuthorQuery] = useState<string | undefined>();

    const [filter, setFilter] = useState<NoteListFilter>({
        minCreationDate: undefined,
        maxCreationDate: undefined,
        minContactDate: undefined,
        maxContactDate: undefined,
        noteTypeList: [],
        authorId: undefined,
        subjectList: [],
        phrase: undefined,
    });

    const { isFetching, isError, data, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery(
        ["notes", props.salesPersonId, props.intermediaryId],
        async ({ pageParam = 0 }) => {
            const pageSize = 10;

            const commonParams: GetNotesParameters = {
                id: 0,
                offset: pageParam,
                limit: pageSize,
                minCreationDate: filter.minCreationDate,
                maxCreationDate: filter.maxCreationDate,
                minContactDate: filter.minContactDate,
                maxContactDate: filter.maxContactDate,
                noteTypeCodeList: filter.noteTypeList.map((x) => x.code),
                authorId: filter.authorId,
                subjectCodeList: filter.subjectList.map((x) => x.code),
                phrase: filter.phrase,
            };

            if (props.salesPersonId) {
                return await NotesService.getSalesPersonNotes({ ...commonParams, id: props.salesPersonId });
            }

            else if (props.intermediaryId) {
                return await NotesService.getIntermediaryNotes({ ...commonParams, id: props.intermediaryId });
            }

            else {
                return await NotesService.getGlobalNotes({ ...commonParams});
            }
        },
        {
            getNextPageParam: (_, pages) =>
                pages[pages.length - 1]?.hasMore
                    ? pages.flatMap((page) => page?.notes.length ?? 0).reduce((a, b) => a + b)
                    : undefined,
        }
    );

    useEffect(() => {
        setIsFirstFetch(true);
        refetch();
    }, [filter]);

    useEffect(() => {
        if (isFetching) return;
        if (isFirstFetch) {
            if (isMobile) {
                setIsFirstFetch(false);
                return;
            }
            const firstNote = data?.pages && data?.pages[0]?.notes ? data?.pages[0]?.notes[0] : undefined;
            props.onNoteSelected(firstNote?.id, firstNote?.entityType, firstNote?.entityId);
        }
        setIsFirstFetch(false);
    }, [isFetching]);

    const isAnyFilter = () => {
        return (
            !!filter.minCreationDate ||
            !!filter.maxCreationDate ||
            !!filter.minContactDate ||
            !!filter.maxContactDate ||
            filter.noteTypeList.length > 0 ||
            filter.subjectList.length > 0 ||
            !!filter.authorId
        );
    };

    const isFilterActive = () => showFiltersPanel || isAnyFilter();

    const clearAllFilters = () => {
        setFilter({
            minCreationDate: undefined,
            maxCreationDate: undefined,
            minContactDate: undefined,
            maxContactDate: undefined,
            noteTypeList: [],
            authorId: undefined,
            subjectList: [],
            phrase: undefined,
        });
        setAuthorQuery("");
    };

    const renderContent = () => {
        return (
            <NotesList
                isMobile={isMobile}
                isError={isError}
                isFetching={isFetching}
                data={data}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                showFiltersPanel={showFiltersPanel}
                selectedNoteId={props.selectedNoteId}
                onNoteSelected={(itemNumber, entityType, entityId) => props.onNoteSelected(itemNumber, entityType, entityId)}
            />
        )
    };

    const getFilterList = () => {
        return (
            <>
                <DateRangePicker
                    label="Utworzono od - do"
                    startDate={filter.minCreationDate}
                    endDate={filter.maxCreationDate}
                    onChange={(min, max) => {
                        setFilter({
                            ...filter,
                            minCreationDate: min ? new Date(min) : undefined,
                            maxCreationDate: max ? new Date(max) : undefined,
                        });
                    }}
                />
                <DateRangePicker
                    label="Data kontaktu od - do"
                    startDate={filter.minContactDate}
                    endDate={filter.maxContactDate}
                    onChange={(min, max) => {
                        setFilter({ ...filter, minContactDate: min, maxContactDate: max });
                    }}
                />
                <MultipleChoiceNoteFilter
                    title="Typ notatki"
                    availableChoices={props.availableNoteTypes?.map((x) => ({ value: x, valueAsString: x.text })) ?? []}
                    selectedChoices={filter.noteTypeList.map((i) => ({ value: i, valueAsString: i.text })) ?? []}
                    setFilter={(selectedItems) => setFilter({ ...filter, noteTypeList: selectedItems })}
                    clearInputDisabled={filter.noteTypeList.length === 0}
                />
                <Flex column style={{ width: "100%" }}>
                    <Text>Autor</Text>
                    <Flex vAlign="center" fill style={{ width: "100%" }}>
                        <Dropdown
                            key={"author-dropdown"}
                            search
                            onChange={(_, data) => {
                                const author = data.value as IAuthor;
                                setFilter({ ...filter, authorId: author?.key ?? undefined})
                                setAuthorQuery(author?.header ?? undefined);
                            }}
                            items={props.availableNoteAuthors?.map((author) => {
                                return { key: author.id, header: author.fullName, description: author.fullName }})}
                            noResultsMessage={pl.common.dropdownNoResultMessage}
                            searchQuery={authorQuery}
                            onSearchQueryChange={(e, data) => {
                                setAuthorQuery(data.searchQuery);
                            }}
                            fluid
                            style={{ width: "100%" }}
                            className={styles.listbox}
                        />
                        <ClearInputIcon
                            disabled={!filter.authorId}
                            onClick={() => {
                                setFilter({ ...filter, authorId: undefined});
                                setAuthorQuery("");
                            }}
                        />
                    </Flex>
                </Flex>
                <MultipleChoiceNoteFilter
                    title="Temat notatki"
                    availableChoices={props.availableNoteRetailSubjects?.map((x) => ({ value: x, valueAsString: x.text })) ?? []}
                    selectedChoices={filter.subjectList.map((i) => ({ value: i, valueAsString: i.text })) ?? []}
                    setFilter={(selectedItems) => setFilter({ ...filter, subjectList: selectedItems })}
                    clearInputDisabled={filter.subjectList.length === 0}
                />
                {!props.isV360View && <Flex column style={{ width: "100%" }}>
                    <Text>Agencja / OFWCA</Text>
                    <PhraseSearchInput
                        searchPhrase={filter.phrase}
                        onSearchPhraseChanged={(phrase) => {
                            setFilter({ ...filter, phrase: phrase});
                        }}
                        clearInputDisabled={!filter.phrase}
                    />
                </Flex>}
            </>
        )
    }

    const renderFilterPanel = () => {
        return (
            <FilterNotesPanel
                isMobile={isMobile}
                isAnyFilter={isAnyFilter()}
                filters={getFilterList()}
                clearAllFilters={() => clearAllFilters()}
            />
        );
    };

    const calculateDesktopHeight = () => {
        return props.isV360View ? "calc(100vh - 200px)" : "calc(100vh - 104px)";
    }

    return (
        <NotesListPanel
            isMobile={isMobile}
            isCreateNewNoteEnable={props.isV360View}
            content={renderContent()}
            filterPanel={renderFilterPanel()}
            isFilterActive={isFilterActive()}
            onAddNoteButtonClicked={() => props.onAddNoteButtonClicked()}
            showFiltersPanel={showFiltersPanel}
            setShowFiltersPanel={(showFilters) => setShowFiltersPanel(showFilters)}
            mobileHeight={"calc(100vh - 30px)"}
            desktopHeight={calculateDesktopHeight()}
        />
    )
};

export default NotesListWrapper;
